import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Skeleton } from '@hungryroot/pho';
import { EDIT_DELIVERY_RECIPES_ROUTE } from '@hungryroot/routes';
import { buttonClickedEvent, pairingInteraction } from '@hungryroot/segment';
import { Button, Heading, Paragraph } from '@hungryroot/ui-kit';

import { EditableFavoritableRdpModalFromPairingId } from '../../../../components/RDP/Modal/EditableFavoritableRdpModalFromPairingId';
import {
  NOVEL_RECIPES_MIN_THRESHOLD,
  useNovelRecipes,
} from '../../../hooks/useNovelRecipes';
import { RecipeCard } from './components/RecipeCard';
import { SkeletonCard } from './components/SkeletonCard';
import styles from './NotEnoughUniqueRecipesDeflection.module.css';

const SEE_MORE_URL = EDIT_DELIVERY_RECIPES_ROUTE;
const SKELETON_COUNT = 8;

function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}

export function NotEnoughUniqueRecipesDeflection() {
  const { recipes, triedCount, novelCount, isLoading, isError } =
    useNovelRecipes();
  const history = useHistory();
  const [openRdpPairingId, setOpenRdpPairingId] = useState<number | null>(null);

  // Fallback: return null so CancellationDeflectionPage falls through to
  // the cancel step when:
  // - any upstream request errored,
  // - we have no display recipes to surface,
  // - or the customer has fewer than NOVEL_RECIPES_MIN_THRESHOLD novel
  //   recipes overall (per the AC: "if fewer than ~12 novel recipes
  //   exist, return null"). Pitching breadth we can't prove reads worse
  //   than just letting them cancel.
  if (
    !isLoading &&
    (isError ||
      recipes.length === 0 ||
      novelCount < NOVEL_RECIPES_MIN_THRESHOLD)
  ) {
    return null;
  }

  function navigateToRecipes() {
    history.push(SEE_MORE_URL);
  }

  return (
    <div className={styles.container}>
      {isLoading ? (
        <>
          <Skeleton className={styles['skeleton-title']} />
          <Skeleton className={styles['skeleton-subcopy']} />
          <Skeleton className={styles['skeleton-subcopy']} />
          <Skeleton className={styles['skeleton-stat']} />
          <div className={styles.grid}>
            {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </>
      ) : (
        <>
          <Heading variant="Hero" className={styles.title}>
            {formatNumber(novelCount)} recipes you haven’t tried
          </Heading>
          <Paragraph className={styles.intro}>
            Based on what you shared with us, it sounds like our recipe
            selection feels stale.
          </Paragraph>
          <Paragraph className={styles.subcopy}>
            Good news: there’s a lot more on the menu than what’s landed in your
            deliveries so far. Every recipe below matches your plan and your
            dietary needs, and our chefs add new recipes every week.
          </Paragraph>

          <div className={styles['stat-callout']}>
            <div className={styles['stat-block']}>
              <Heading variant="H1" className={styles['stat-number-tried']}>
                {formatNumber(triedCount)}
              </Heading>
              <Paragraph className={styles['stat-label']}>
                recipes you’ve tried
              </Paragraph>
            </div>
            <div className={styles['stat-divider']} aria-hidden="true" />
            <div className={styles['stat-block']}>
              <Heading variant="H1" className={styles['stat-number-novel']}>
                {formatNumber(novelCount)}
              </Heading>
              <Paragraph className={styles['stat-label']}>
                recipes not yet tried
              </Paragraph>
            </div>
          </div>

          <Heading variant="H4" className={styles.heading}>
            A taste of what’s waiting for you
          </Heading>

          <div className={styles.grid}>
            {recipes.map((recipe) => (
              <RecipeCard
                key={recipe.id}
                recipe={recipe}
                onClick={() => {
                  pairingInteraction({
                    pairingId: recipe.id,
                    pairingName: recipe.name,
                    actionType: 'clicked',
                    location: 'card',
                  });
                  setOpenRdpPairingId(recipe.id);
                }}
              />
            ))}
          </div>

          <div className={styles['cta-container']}>
            <Button
              onClick={() => {
                buttonClickedEvent({
                  button_name: 'See all recipes',
                  order_id: null,
                  page_position: 'cancel_deflection_not_enough_unique_recipes',
                });
                navigateToRecipes();
              }}
              style="default"
              size="large"
              prominence="primary"
              textPrimary={`See all ${formatNumber(novelCount)} recipes`}
            />
          </div>
        </>
      )}

      {openRdpPairingId !== null && (
        <EditableFavoritableRdpModalFromPairingId
          pairingId={openRdpPairingId}
          onDismiss={() => setOpenRdpPairingId(null)}
        />
      )}
    </div>
  );
}
